import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Plus, Trash2 } from 'lucide-react';
import { v3Api } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { Field, Text, Check } from './forms/fields';
import './workpermit.css';

/**
 * Work Permit template builder. Defines a named permit type for the
 * concessionaire: ordered approver roles + the sub-forms (components) tenants
 * must fill. Concessionaire scope is enforced server-side from the JWT.
 */
export default function WorkPermitConfig() {
    const { t } = useTranslation();
    const { concessionaireId } = useAuth();

    const [name, setName] = useState('');
    const [approvers, setApprovers] = useState([{ user_role: '' }]);
    const [catalog, setCatalog] = useState([]);          // [{ id, name }]
    const [selected, setSelected] = useState([]);        // component ids
    const [templates, setTemplates] = useState([]);
    const [saving, setSaving] = useState(false);

    const loadTemplates = useCallback(() => {
        v3Api.workPermit.templates().then((rows) => setTemplates(Array.isArray(rows) ? rows : [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!concessionaireId) return;
        v3Api.workPermit.components().then((rows) => setCatalog(Array.isArray(rows) ? rows : [])).catch(() => {});
        loadTemplates();
    }, [concessionaireId, loadTemplates]);

    const setApproverRole = (i, val) => setApprovers((prev) => prev.map((a, idx) => idx === i ? { user_role: val } : a));
    const addApprover = () => setApprovers((prev) => [...prev, { user_role: '' }]);
    const removeApprover = (i) => setApprovers((prev) => prev.filter((_, idx) => idx !== i));
    const toggleComponent = (id, on) => setSelected((prev) => on ? [...prev, id] : prev.filter((x) => x !== id));

    const submit = async () => {
        if (!name.trim()) { toast.error(t('Work permit name is required')); return; }
        setSaving(true);
        try {
            const payload = {
                name: name.trim(),
                approvers: approvers
                    .filter((a) => a.user_role.trim())
                    .map((a, idx) => ({ level: String(idx + 1), user_role: a.user_role.trim() })),
                components: selected.map((id) => {
                    const c = catalog.find((x) => x.id === id);
                    return { component_id: id, name: c ? c.name : '' };
                }),
                documents: [],
            };
            await v3Api.workPermit.configCreate(payload);
            toast.success(t('Work Permit template created'));
            setName('');
            setApprovers([{ user_role: '' }]);
            setSelected([]);
            loadTemplates();
        } catch (e) {
            toast.error(e.response?.data?.message || t('Error creating template'));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            await v3Api.workPermit.configDelete(id);
            toast.success(t('Template deleted'));
            loadTemplates();
        } catch (e) {
            toast.error(e.response?.data?.message || t('Failed to delete'));
        }
    };

    return (
        <div className="v3 v3-wp">
            <div className="v3-wp-header">
                <div>
                    <h1 className="v3-wp-title">{t('Work Permit / NOC Configuration')}</h1>
                    <div className="v3-wp-subtitle">{t('Define permit types, their approver hierarchy and required forms.')}</div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="v3-wp-config-grid">
                {/* Builder */}
                <div className="v3-wp-card">
                    <div className="v3-wp-card-head"><h6 className="v3-wp-card-title">{t('Add New Work Permit')}</h6></div>

                    <div className="v3-wp-grid">
                        <Field label="Work Permit Name" required full>
                            <Text value={name} onChange={setName} placeholder={t('e.g. Hot Work Permit')} />
                        </Field>
                    </div>

                    <hr className="v3-wp-divider" />
                    <div className="v3-wp-label" style={{ marginBottom: 8 }}>{t('Approver Hierarchy (in order)')}</div>
                    {approvers.map((a, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                            <span className="v3-wp-muted" style={{ width: 56 }}>{t('Level')} {i + 1}</span>
                            <input className="v3-wp-input" value={a.user_role} placeholder={t('User role (e.g. Operations Manager)')} onChange={(e) => setApproverRole(i, e.target.value)} />
                            {approvers.length > 1 && (
                                <button className="v3-wp-iconbtn" onClick={() => removeApprover(i)} title={t('Remove')}><Trash2 size={16} /></button>
                            )}
                        </div>
                    ))}
                    <button className="v3-btn" onClick={addApprover}><Plus size={14} /> {t('Add Approver')}</button>

                    <hr className="v3-wp-divider" />
                    <div className="v3-wp-label" style={{ marginBottom: 8 }}>{t('Required Components')}</div>
                    <div className="v3-wp-checks">
                        {catalog.map((c) => (
                            <Check key={c.id} id={`cfg-${c.id}`} label={c.name} checked={selected.includes(c.id)} onChange={(on) => toggleComponent(c.id, on)} />
                        ))}
                    </div>

                    <div className="v3-wp-rowfoot" style={{ marginTop: 18 }}>
                        <button className="v3-btn v3-btn-primary" onClick={submit} disabled={saving}>{saving ? t('Saving…') : t('Create Work Permit')}</button>
                    </div>
                </div>

                {/* Existing templates */}
                <div className="v3-wp-card">
                    <div className="v3-wp-card-head"><h6 className="v3-wp-card-title">{t('Existing Templates')}</h6></div>
                    <table className="v3-wp-table">
                        <thead><tr><th>#</th><th>{t('Name')}</th><th>{t('Components')}</th><th></th></tr></thead>
                        <tbody>
                            {templates.length === 0 ? (
                                <tr><td colSpan="4" className="v3-wp-empty">{t('No templates yet')}</td></tr>
                            ) : templates.map((tpl, idx) => (
                                <tr key={tpl._id}>
                                    <td>{idx + 1}</td>
                                    <td>{tpl.name}</td>
                                    <td className="v3-wp-muted">{(tpl.components || []).map((c) => c.name).join(', ') || '—'}</td>
                                    <td><button className="v3-wp-iconbtn" onClick={() => remove(tpl._id)} title={t('Delete')}><Trash2 size={16} /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
